import { BaseRequest } from '../store/models'
import {
  insightsApi,
  type InsightsDataApiResponse,
  type ScoreResultMeetingRef,
  type UserScoreResultRow,
  type UserScoreRow,
} from '../shared/insights/store/insights'
import { INSIGHTS_CACHE_TTL_SECONDS } from '../shared/insights/utils/insightsUtils'

export interface InsightsDataRequest extends BaseRequest {
  from: string
  to: string
  userIdList?: string[]
  charts?: string[]
}

interface AdvisorInsightsDataRequest extends BaseRequest {
  from: string
  to: string
  meetingTemplateId?: string
  userIdList?: string[]
  charts?: string[]
}

// Some array fields come back as a JSON-encoded string rather than a nested array
const parseJsonArray = <T>(raw: unknown): T[] => {
  if (Array.isArray(raw)) {
    return raw
  }
  if (typeof raw !== 'string') {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

type ScoreResultMeetingField = {
  [Field in keyof UserScoreResultRow]: UserScoreResultRow[Field] extends ScoreResultMeetingRef[] ? Field : never
}[keyof UserScoreResultRow]

const SCORE_RESULT_MEETING_FIELDS: ScoreResultMeetingField[] = [
  'wf_nextStepsImprovementSessions',
  'wf_lifeEventsImprovementSessions',
  'wf_followUpTasksImprovementSessions',
  'wf_meetingAgendaImprovementSessions',
  'wf_coreMotivatorsImprovementSessions',
  'wf_decisionsAgreedImprovementSessions',
  'wf_decisionsNotAgreedImprovementSessions',
  'eng_sentimentImprovementSessions',
  'eng_avgTalkTimeImprovementSessions',
  'eng_questionRateImprovementSessions',
  'eng_clientQuestionsImprovementSessions',
  'eng_clientConnectionImprovementSessions',
  'eng_personalEngagementImprovementSessions',
  'eng_avoidedLongMonologueImprovementSessions',
  'eng_interactiveConversationImprovementSessions',
  'sch_calendarEventImprovementSessions',
  'sch_overUnderTimeImprovementSessions',
  'sch_futureMeetingsImprovementSessions',
  'top_meetings',
]

const parseScoreResultRow = (row: UserScoreResultRow): UserScoreResultRow => {
  const parsedMeetingFields = Object.fromEntries(
    SCORE_RESULT_MEETING_FIELDS.map((field) => [field, parseJsonArray<ScoreResultMeetingRef>(row[field])]),
  ) as Record<ScoreResultMeetingField, ScoreResultMeetingRef[]>
  return { ...row, ...parsedMeetingFields }
}

const remapRequestedChartKey = (charts: Record<string, unknown>, requestedKey: string, baseKey: string): void => {
  if (requestedKey === baseKey || !(requestedKey in charts)) {
    return
  }
  charts[baseKey] = charts[requestedKey]
  delete charts[requestedKey]
}

const normalizeInsightsDataApiResponse = (
  response: { data?: InsightsDataApiResponse } & InsightsDataApiResponse,
  _meta: unknown,
  arg: { charts?: string[] },
): InsightsDataApiResponse => {
  const data = response.data ?? response
  const charts = data.charts
  if (!charts || !arg.charts?.length) {
    return data
  }

  const remappedCharts: Record<string, unknown> = { ...charts }
  arg.charts.forEach((requestedKey) => {
    if (requestedKey.startsWith('user_score_result_list')) {
      remapRequestedChartKey(remappedCharts, requestedKey, 'user_score_result_list')
    } else if (requestedKey.startsWith('user_score_list')) {
      remapRequestedChartKey(remappedCharts, requestedKey, 'user_score_list')
    }
  })

  const userScorecardList = remappedCharts.user_score_list as UserScoreRow[] | undefined
  const userScoreResultList = remappedCharts.user_score_result_list as UserScoreResultRow[] | undefined

  return {
    ...data,
    charts: {
      ...remappedCharts,
      ...(userScorecardList ? { user_score_list: userScorecardList.map((row) => ({ ...row, score_history: parseJsonArray(row.score_history) })) } : {}),
      ...(userScoreResultList ? { user_score_result_list: userScoreResultList.map(parseScoreResultRow) } : {}),
    },
  }
}

const insightsDataApi = insightsApi.injectEndpoints({
  endpoints: (builder) => ({
    getInsightsData: builder.query<InsightsDataApiResponse, InsightsDataRequest>({
      query: ({ accountId, from, to, userIdList, charts }) => ({
        url: `/v1/accounts/${accountId}/insights/query`,
        method: 'POST',
        body: {
          from,
          to,
          ...(userIdList?.length ? { userIdList } : {}),
          ...(charts?.length ? { charts } : {}),
        },
      }),
      transformResponse: normalizeInsightsDataApiResponse,
      keepUnusedDataFor: INSIGHTS_CACHE_TTL_SECONDS,
    }),
    getAdvisorInsightsData: builder.query<InsightsDataApiResponse, AdvisorInsightsDataRequest>({
      query: ({ accountId, from, to, meetingTemplateId, userIdList, charts }) => ({
        url: `/v1/accounts/${accountId}/insights/advisor-query`,
        method: 'GET',
        params: {
          from,
          to,
          ...(meetingTemplateId ? { meetingTemplateId } : {}),
          ...(userIdList?.length ? { userIdList } : {}),
          ...(charts?.length ? { charts } : {}),
        },
      }),
      transformResponse: normalizeInsightsDataApiResponse,
      keepUnusedDataFor: INSIGHTS_CACHE_TTL_SECONDS,
    }),
  }),
})

export const { useGetInsightsDataQuery, useGetAdvisorInsightsDataQuery } = insightsDataApi
